import React, { useState, useEffect } from "react";
import { Search, X, MapPin, Calendar, Globe, Building } from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";

export default function BossStatisticPlaces() {
  const { t } = useTranslation();
  const history = useHistory();

  const [allData, setAllData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const currentMonth = moment().format("MM");
  const currentYear = moment().format("YYYY");

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    if (data) setAllData(JSON.parse(data));
  }, []);

  if (!allData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const places = allData.myPlaces || [];

  // Filter: active only + search
  const filteredPlaces = places
    .filter((p) => p.active === 1)
    .filter((p) =>
      searchQuery.trim()
        ? p.place.toLowerCase().includes(searchQuery.toLowerCase())
        : true,
    );

  // ── Place stats (current month) ───────────────────────────────────────────
  const getPlaceStats = (place) => {
    const placeId = place.id;

    const placeWork = (allData.myDailyWork || []).filter((work) => {
      const parts = work.date?.split("-");
      if (!parts || parts.length !== 3) return false;
      const [, month, year] = parts;
      return (
        work.placeId === placeId &&
        month === currentMonth &&
        year === currentYear
      );
    });

    const totalDailySalaries = placeWork.reduce(
      (s, w) => s + (parseFloat(w.salary) || 0),
      0,
    );

    const placeMonthlySalaries = Array.isArray(allData?.myMonthlySalary)
      ? allData.myMonthlySalary.filter((item) => {
          const parts = item.date?.split("-");
          if (!parts || parts.length !== 3) return false;
          const [, month, year] = parts;
          return (
            item.placeId === placeId &&
            month === currentMonth &&
            year === currentYear
          );
        })
      : [];

    const totalMonthlySalaries = placeMonthlySalaries.reduce((s, item) => {
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return s + (emp?.monthlySalary || 0);
    }, 0);

    const totalSalaries = totalDailySalaries + totalMonthlySalaries;

    const workDays = new Set(placeWork.map((w) => w.date)).size;

    const placeExpenses = (allData.myExpenses || []).filter((expense) => {
      const parts = expense.date?.split("-");
      if (!parts || parts.length !== 3) return false;
      const [, month, year] = parts;
      return (
        expense.placeId === placeId &&
        month === currentMonth &&
        year === currentYear
      );
    });

    const totalExpenses = placeExpenses.reduce(
      (s, e) => s + (parseFloat(e.price) || 0),
      0,
    );

    let revenue = 0;
    if (place.paidWay === 2) {
      revenue = placeWork.reduce(
        (s, w) => s + (parseFloat(w.placeDailySalary) || 0),
        0,
      );
    } else {
      revenue = parseFloat(place.profit || 0);
    }

    const netProfit = revenue - totalSalaries - totalExpenses;

    return { revenue, totalSalaries, totalExpenses, workDays, netProfit };
  };

  // ── Navigate to place detail ──────────────────────────────────────────────
  const handlePlacePress = (place) => {
    localStorage.setItem("choosedPlace", JSON.stringify(place));
    if (place.placeType === 2) {
      history.push("/BossStatisticTemporaryPlaceData");
    } else {
      history.push("/boss-statistic-places-data");
    }
  };

  // Payment type badge
  const paymentBadge = (paidWay) => {
    if (paidWay === 2)
      return {
        label: t("daily"),
        Icon: Calendar,
        bg: "bg-green-50",
        text: "text-green-600",
      };
    if (paidWay === 3)
      return {
        label: t("global"),
        Icon: Globe,
        bg: "bg-blue-50",
        text: "text-blue-600",
      };
    return {
      label: t("monthly"),
      Icon: Calendar,
      bg: "bg-purple-50",
      text: "text-purple-600",
    };
  };

  return (
    <div className="min-h-screen bg-transparent pb-28">
      {/* ── Search bar ── */}
      <div className="mx-4 mt-4 mb-3 flex items-center bg-white rounded-xl px-3 py-2.5 shadow-sm border border-gray-100">
        <Search className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by place name..."
          className="flex-1 text-sm text-gray-800 outline-none bg-transparent"
        />
        {searchQuery.length > 0 && (
          <button
            onClick={() => setSearchQuery("")}
            className="ml-2 p-1 hover:bg-gray-100 rounded-full transition flex-shrink-0"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* ── Empty state ── */}
      {filteredPlaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-10">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-5">
            <Building className="w-12 h-12 text-gray-300" />
          </div>
          <p className="text-lg font-semibold text-gray-500 mb-2 text-center">
            {searchQuery ? t("noPlacesMatchSearch") : t("noPlacesYet")}
          </p>
          <p className="text-sm text-gray-400 text-center leading-relaxed">
            {searchQuery
              ? t("tryDifferentSearch")
              : t("addPlacesToTrack")}
          </p>
        </div>
      ) : (
        /* ── 2-column grid ── */
        <div className="px-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            {filteredPlaces.map((item) => {
              const badge = paymentBadge(item.paidWay);
              const { Icon: BadgeIcon } = badge;

              return (
                <button
                  key={item.id}
                  onClick={() => handlePlacePress(item)}
                  className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 text-left hover:shadow-md transition"
                >
                  {/* Card header: avatar + info */}
                  <div className="flex items-center gap-2">
                    {/* Avatar */}
                    <div className="w-9 h-9 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {item.place}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-gray-400 font-medium">
                          ID: {item.id}
                        </span>
                        {item.paidWay && (
                          <span
                            className={`flex items-center gap-0.5 text-[8px] font-semibold px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}
                          >
                            <BadgeIcon className="w-2 h-2" />
                            {badge.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
